import React, { useState } from 'react';
import { useGetAllArtists } from '../../hooks/useGetAllArtists';
import {
  Box,
  Typography,
  FormControl,
  Button,
  Grid,
  Paper,
} from '@mui/material';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import { ArtistSummaryListItem } from '../../@types/api';
import LocalisedDatePicker from '../../components/Common/LocalisedDatePicker';
import dayjs, { Dayjs } from 'dayjs';
import RestUtilities from '../../services/RestUtilities';
import { colDateFormat2, dbDayFormat } from '../../Constants';

const formControlSxProps = { mx: 1, minWidth: '210px' };

const TurnoverReport = () => {
  const [startDate, setStartDate] = useState<Dayjs | null>(
    dayjs().startOf('month')
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs().endOf('month'));
  const [data, setData] = useState<ArtistSummaryListItem[]>([]);

  useGetAllArtists();

  const handleFetchData = async () => {
    var res = await RestUtilities.get(
      `/api/booking/GetTurnoverData?dateFrom=${startDate?.format(
        dbDayFormat
      )}&dateTo=${endDate?.format(dbDayFormat)}`
    );
    if (res.ok) {
      setData(res.content);
    }
  };

  const sum = (field: string) => {
    var result = data.reduce((p: number, c: any) => p + c[field], 0);
    return result.toFixed(0);
  };

  return (
    <Box>
      <Box my={1} display='flex' justifyContent={'flex-start'}>
        <Typography variant='h5'>Turnover</Typography>
        <Box sx={{ ml: 2 }} display={'flex'}>
          <FormControl size='small' sx={formControlSxProps}>
            <LocalisedDatePicker
              label='StartDate'
              date={startDate}
              setDate={setStartDate}
            />
          </FormControl>
          <FormControl size='small' sx={formControlSxProps}>
            <LocalisedDatePicker
              label='EndDate'
              date={endDate}
              setDate={setEndDate}
            />
          </FormControl>
          <Button
            size='small'
            color='primary'
            variant='contained'
            onClick={handleFetchData}
            sx={{ mr: 1 }}
          >
            Show Data
          </Button>
        </Box>
      </Box>
      <Paper sx={{ p: 1, mb: 2 }}>
        <Typography component='h2' variant='h6' color={'primary'} gutterBottom>
          Totals
        </Typography>
        <Grid container>
          <Grid item sx={{ ml: 0 }}>
            Payments: {data.length}
          </Grid>
          <Grid item sx={{ ml: 2 }}>
            Fees: £{sum('feeAmount')}
          </Grid>
          <Grid item sx={{ ml: 2 }}>
            VAT: £{sum('vatAmount')}
          </Grid>
          <Grid item sx={{ ml: 2 }}>
            Total Fees (inc VAT): £{sum('totalFee')}
          </Grid>
          <Grid item sx={{ ml: 2 }}>
            Total Paid: £{sum('paymentAmount')}
          </Grid>
        </Grid>
      </Paper>
      <Box
        sx={{ display: 'flex', flexDirection: 'column', minHeight: 200, mb: 2 }}
      >
        <DataGrid
          rows={data}
          getRowId={(row) => row.feePaymentId}
          slots={{ toolbar: GridToolbar }}
          columns={
            [
              { headerName: 'ID', field: 'feePaymentId', width: 60 },
              {
                headerName: 'EventDate',
                field: 'eventDate',
                width: 80,
                valueGetter: (params) =>
                  dayjs(params.value).format(colDateFormat2),
              },
              {
                headerName: 'PaymentDate',
                field: 'paymentDate',
                width: 80,
                valueGetter: (params) =>
                  dayjs(params.value).format(colDateFormat2),
              },
              {
                headerName: 'Client',
                width: 200,
                field: 'clientName',
              },
              {
                headerName: 'Fee',
                field: 'feeAmount',
                width: 70,
              },
              {
                headerName: 'VAT',
                field: 'vatAmount',
                width: 60,
              },
              {
                headerName: 'Rate',
                field: 'vatRate',
                width: 60,
              },
              {
                headerName: 'Total',
                field: 'totalFee',
                width: 60,
              },
              {
                headerName: 'Paid',
                field: 'paymentAmount',
                width: 60,
              },
              {
                headerName: 'Balance',
                field: 'outstanding',
                width: 60,
              },
              {
                headerName: 'Status',
                field: 'statusDescription',
                width: 100,
              },
              {
                headerName: 'FeeType',
                field: 'feeTypeDescription',
              },
              {
                headerName: 'Method',
                field: 'paymentMethodDescription',
              },
              {
                headerName: 'Artist',
                field: 'artistName',
              },
              {
                headerName: 'Booked',
                field: 'bookedWhen',
                width: 80,
                valueGetter: (params) =>
                  dayjs(params.value).format(colDateFormat2),
              },
            ] as GridColDef[]
          }
          density='compact'
          pageSizeOptions={[25, 50, 100, 250]}
        />
      </Box>
    </Box>
  );
};
export default TurnoverReport;
